// ChatBox.jsx
import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Button,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FaWhatsapp, FaPaperPlane } from "react-icons/fa";

const BOT_URL = process.env.REACT_APP_BOT_URL;
const WA_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER;

export default function ChatBox({ onClose }) {
  const [messages, setMessages] = useState([
    { from: "bot", text: "¡Hola! 😊 ¿En qué podemos ayudarte?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef();

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    setMessages((ms) => [...ms, { from: "user", text }]);
    setInput("");
    setLoading(true);
    try {
      const res = await fetch(BOT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text }),
      });
      if (!res.ok) throw new Error(res.status);
      const { reply } = await res.json();
      setMessages((ms) => [...ms, { from: "bot", text: reply }]);
    } catch {
      setMessages((ms) => [
        ...ms,
        { from: "bot", text: "Error conectando al bot." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const openWhatsApp = () => {
    const transcript = messages
      .map((m) => (m.from === "user" ? `Tú: ${m.text}` : `Soporte: ${m.text}`))
      .join("\n");
    window.open(
      `https://api.whatsapp.com/send?phone=${WA_NUMBER}&text=${encodeURIComponent(
        transcript
      )}`,
      "_blank"
    );
  };

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 96,
        right: 24,
        width: 320,
        zIndex: 2000,
      }}
    >
      <Paper
        elevation={8}
        sx={{ display: "flex", flexDirection: "column", height: 440 }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            bgcolor: "#e06a00",
            color: "#fff",
            px: 2,
            py: 1,
          }}
        >
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Soporte
          </Typography>
          <IconButton onClick={onClose} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </Box>
        {/* Mensajes */}
        <Box sx={{ flexGrow: 1, overflowY: "auto", p: 1 }}>
          <List disablePadding>
            {messages.map((m, i) => (
              <ListItem
                key={i}
                sx={{
                  justifyContent: m.from === "user" ? "flex-end" : "flex-start",
                  py: 0.5,
                }}
              >
                <Paper
                  sx={{
                    p: 1,
                    bgcolor: m.from === "user" ? "#ffeedd" : "#f5f5f5",
                    maxWidth: "80%",
                    boxShadow: 1,
                  }}
                >
                  <ListItemText
                    primary={m.text}
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </Paper>
              </ListItem>
            ))}
            <div ref={endRef} />
          </List>
        </Box>
        <Divider />
        {/* Input + WhatsApp */}
        <Box sx={{ p: 1 }}>
          <Button
            fullWidth
            startIcon={<FaWhatsapp />}
            onClick={openWhatsApp}
            sx={{
              bgcolor: "#25D366",
              color: "#fff",
              textTransform: "none",
              mb: 1,
              "&:hover": { bgcolor: "#20b859" },
            }}
          >
            Abrir en WhatsApp
          </Button>
          <Box sx={{ display: "flex" }}>
            <TextField
              size="small"
              placeholder="Escribe tu mensaje..."
              fullWidth
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              disabled={loading}
            />
            <IconButton
              onClick={send}
              disabled={loading || !input.trim()}
              sx={{ color: "#e06a00" }}
            >
              <FaPaperPlane />
            </IconButton>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
